import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  AddressTreeStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { AddressService } from "src/service/address.service";
import {
  addressTreeLoadErrorAction,
  addressTreeLoadLastModifiedFromApiAction,
  addressTreeLoadListFromApiAction,
  addressTreeLoadSuccessAction,
  addressTreeUnsetKeyStorage,
} from "../actions/address.action";
import { treeAddress } from "../selectors/address.selectors";
import { currentUserData } from "../selectors/authentication.selectors";

/**
 * Handle generic side ngrx effect from addressTree action
 */
@Injectable()
export class AddressEffects {
  constructor(
    private _actions$: Actions,
    private _addressTreeService: AddressService,
    private _store: Store
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public addressTreeUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => addressTreeUnsetKeyStorage())
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public addressTreeStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AddressTreeStoreAction.ADDRESS_TREE_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(treeAddress)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const addressTreeObject: GenericStoreApiData = combinedObject[2];

        if (addressTreeObject?.data) {
          return addressTreeLoadLastModifiedFromApiAction({
            addressTreeObject: addressTreeObject.data,
          });
        } else {
          return addressTreeLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of addressTrees effect
   */
  public addressTreeListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AddressTreeStoreAction.ADDRESS_TREE_LOAD_FROM_API),
      withLatestFrom(
        this._store.select(currentUserData)
      ),
      switchMap((currentObject: any) => {
        return this._addressTreeService.getAddressTree((currentObject[1] as GenericUserData).currentUser.id).pipe(
          map((addressTreeObject: GenericApiData) =>
            addressTreeLoadSuccessAction({ addressTreeObject })
          ),
          catchError(() => of(addressTreeLoadErrorAction()))
        );
      })
    )
  );

  /**
   * Load full list of addressTree by last modified effect
   */
  public addressTreeListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AddressTreeStoreAction.ADDRESS_TREE_LOAD_LAST_MODIFIED_FROM_API),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(treeAddress)
      ),
      switchMap((combinedObject: any) => {
        return this._addressTreeService.getAddressTreeByLastModified(
          (combinedObject[1] as GenericUserData).currentUser.id,
          (combinedObject[2] as GenericStoreApiData)?.data).pipe(
            map((addressTreeObject: GenericApiData) =>
              addressTreeLoadSuccessAction({ addressTreeObject })
            ),
            catchError(() => of(addressTreeLoadErrorAction()))
          );
      })
    )
  );
}
